import { iff } from '@digibee/control-statements';
import { cn } from '@heroui/react';
import {
  Bell,
  Coins,
  Group,
  Lock,
  OpenNewWindow,
  Settings,
  ShieldEye,
  WindowLock
} from 'iconoir-react';
import { FC, useState } from 'react';
import { useSelector } from 'react-redux';

import * as Elements from './Sidebar.elements';

import { TOGAI_ORGANIZATION_ID, TOGAI_URL } from '~/common/helpers/environment';
import featureFlagConstants from '~/common/helpers/featureFlagConstants';
import i18n from '~/common/helpers/i18n';
import useContainPaths from '~/common/hooks/useContainPaths';
import useFeatureFlag from '~/common/hooks/useFeatureFlag';
import { usePlatformLogo } from '~/common/hooks/usePlatformLogo';
import paths from '~/common/jsons/paths.json';
import theme from '~/common/styled/theme';
import Role from '~/components/Role';
import { StoreRootState } from '~/scenes/Governance/scenes/types/StoreRootState';

interface Props {
  realm: string;
  realmData: any;
  license: any;
  showTogaiRedirectButton: boolean;
  togaiToken: string;
}

const Sidebar: FC<Props> = ({
  realm,
  realmData,
  license,
  showTogaiRedirectButton,
  togaiToken
}) => {
  const { sidebarLogo, isPowered } = usePlatformLogo();

  const containPaths = useContainPaths();
  const [pathName, setPathName] = useState(`${realm}${paths.settings}`);
  const { application } = useSelector((state: StoreRootState) => state);
  const { treatments } = useFeatureFlag([
    featureFlagConstants.BUILD_GLOBALS,
    featureFlagConstants.BUILD_ACCOUNTS
  ]);
  const isBuildGlobalsEnabled =
    treatments[featureFlagConstants.BUILD_GLOBALS]?.treatment === 'on';
  const isBuildAccountsEnabled =
    treatments[featureFlagConstants.BUILD_ACCOUNTS]?.treatment === 'on';

  return (
    <div
      className='flex h-full w-[216px] flex-col gap-4 border-r border-solid border-gray-300'
      data-name={pathName}
    >
      <div className='px-4 pt-4'>
        <img
          src={sidebarLogo}
          className={cn('cursor-pointer', !isPowered ? 'w-[121.5px]' : null)}
          alt='Digibee Logo'
        />
      </div>
      <div className='h-full overflow-auto px-4'>
        <Role name='ORG:MANAGER'>
          <div
            className='mb-4 mt-8 flex items-center gap-2'
            data-testid='organization'
          >
            <Group className='icon-smallest' data-testid='icon' />
            <Elements.Title
              data-testid='text'
              data-active={containPaths([
                `${realm}${paths.organization_groups}`
              ])}
              align='left'
              size='lg'
              weight='medium'
            >
              {i18n.t('scenes.beta.labels.organization')}
            </Elements.Title>
          </div>
          <Elements.WrapperLink>
            <Elements.ContentLink
              data-testid='governance-organization-groups'
              data-active={containPaths([
                `${realm}${paths.organization_groups}`
              ])}
            >
              <Elements.Link
                data-testid='link'
                to={`/${realm}${paths.organization_groups}`}
              >
                <Elements.TextLink
                  data-testid='text'
                  align='left'
                  weight='regular'
                  size='md'
                  data-active={containPaths([
                    `/${realm}${paths.organization_groups}`
                  ])}
                >
                  {i18n.t('scenes.beta.labels.organization_groups')}
                </Elements.TextLink>
              </Elements.Link>
            </Elements.ContentLink>
          </Elements.WrapperLink>
        </Role>

        <div
          className='mb-4 mt-8 flex items-center gap-2'
          data-testid='administration'
        >
          <Lock className='icon-smallest' data-testid='icon' />
          <Elements.Title
            data-testid='text'
            data-active={containPaths([
              `${realm}${paths.user}`,
              `${realm}${paths.audit}`,
              `${realm}${paths.ctl}`,
              `${realm}${paths.groups}`,
              `${realm}${paths.token}`,
              `${realm}${paths.beta_user}`,
              `${realm}${paths.roles}`,
              `${realm}${paths.identity_provider}`
            ])}
            align='left'
            size='lg'
            weight='medium'
          >
            {i18n.t('scenes.beta.labels.administration')}
          </Elements.Title>
        </div>
        <Elements.WrapperLink>
          <Role name='AUDIT:READ'>
            <Elements.ContentLink
              data-testid='audit-administration-audit'
              data-active={containPaths([`${realm}${paths.audit}`])}
            >
              <Elements.Link data-testid='link' to={`/${realm}${paths.audit}`}>
                <Elements.TextLink
                  data-testid='text'
                  align='left'
                  weight='regular'
                  size='md'
                  data-active={containPaths([`/${realm}${paths.audit}`])}
                >
                  {i18n.t('scenes.beta.labels.audit')}
                </Elements.TextLink>
              </Elements.Link>
            </Elements.ContentLink>
          </Role>
          <Role name={['USER:READ']}>
            <Elements.ContentLink
              data-testid='user-administration-user'
              data-active={containPaths([`${realm}${paths.beta_user}`])}
            >
              <Elements.Link
                data-testid='link'
                to={`/${realm}${paths.beta_user}`}
                onClick={() => setPathName(`${realm}${paths.beta_user}`)}
                flex
                data-active={containPaths([`${realm}${paths.beta_user}`])}
              >
                <Elements.TextLink
                  data-testid='text'
                  data-active={containPaths([`${realm}${paths.beta_user}`])}
                  align='left'
                  weight='regular'
                  size='md'
                >
                  {`${i18n.t('noun.user_plural')}`}
                </Elements.TextLink>
                {iff(realmData?.oldPermissionRevoked === false, () => (
                  <Elements.Badger>Beta</Elements.Badger>
                ))}
              </Elements.Link>
            </Elements.ContentLink>
          </Role>
          <Role name={['GROUP:READ']}>
            <Elements.ContentLink
              data-testid='groups-administration-groups'
              data-active={containPaths([`${realm}${paths.groups}`])}
            >
              <Elements.Link
                data-testid='link'
                to={`/${realm}${paths.groups}/list`}
                onClick={() => setPathName(`${realm}${paths.groups}`)}
                flex
                data-active={containPaths([`${realm}${paths.groups}`])}
              >
                <Elements.TextLink
                  data-testid='text'
                  data-active={containPaths([`${realm}${paths.groups}`])}
                  align='left'
                  weight='regular'
                  size='md'
                >
                  {i18n.t('scenes.groups.labels.groups')}
                </Elements.TextLink>
                {iff(realmData?.oldPermissionRevoked === false, () => (
                  <Elements.Badger>Beta</Elements.Badger>
                ))}
              </Elements.Link>
            </Elements.ContentLink>
          </Role>
          <Role name={['ROLE:READ']}>
            <Elements.ContentLink
              data-testid='roles-administration-roles'
              data-active={containPaths([`${realm}${paths.roles}`])}
            >
              <Elements.Link
                data-testid='link'
                to={`/${realm}${paths.roles}`}
                onClick={() => setPathName(`${realm}${paths.roles}`)}
                flex
                data-active={containPaths([`${realm}${paths.roles}`])}
              >
                <Elements.TextLink
                  data-testid='text'
                  data-active={containPaths([`${realm}${paths.roles}`])}
                  align='left'
                  weight='regular'
                  size='md'
                >
                  {i18n.t('scenes.roles.labels.roles')}
                </Elements.TextLink>
                {iff(realmData?.oldPermissionRevoked === false, () => (
                  <Elements.Badger>Beta</Elements.Badger>
                ))}
              </Elements.Link>
            </Elements.ContentLink>
          </Role>
          <Role name={['SSO-CONFIGURATION:READ']}>
            <Elements.ContentLink
              data-testid='accesses_via_idp-administration-accesses_via_idp'
              data-active={containPaths([`${realm}${paths.identity_provider}`])}
            >
              <Elements.Link
                data-testid='link'
                to={`/${realm}${paths.identity_provider}/idp-settings`}
                onClick={() =>
                  setPathName(`${realm}${paths.identity_provider}`)
                }
                flex
                data-active={containPaths([
                  `${realm}${paths.identity_provider}`
                ])}
              >
                <Elements.TextLink
                  data-testid='text'
                  data-active={containPaths([
                    `${realm}${paths.identity_provider}`
                  ])}
                  align='left'
                  weight='regular'
                  size='md'
                >
                  {i18n.t('action.identitiy_provider')}
                </Elements.TextLink>
              </Elements.Link>
            </Elements.ContentLink>
          </Role>
          {iff(realmData?.oldPermissionRevoked === false, () => (
            <Role name={['USER:READ']}>
              <Elements.ContentLink
                data-active={containPaths([`${realm}${paths.user}`])}
              >
                <Elements.Link
                  to={`/${realm}${paths.user}`}
                  onClick={() => setPathName(`${realm}${paths.user}`)}
                  data-active={containPaths([`${realm}${paths.user}`])}
                >
                  <Elements.TextLink
                    data-active={containPaths([`${realm}${paths.user}`])}
                    align='left'
                    weight='regular'
                    size='md'
                  >
                    {i18n.t('noun.user_plural')}
                  </Elements.TextLink>
                </Elements.Link>
              </Elements.ContentLink>
            </Role>
          ))}
          <Role name={['USER:READ:LIST-JWT']}>
            <Elements.ContentLink
              data-active={containPaths([`${realm}${paths.token}`])}
            >
              <Elements.Link
                to={`/${realm}${paths.token}`}
                onClick={() => setPathName(`${realm}${paths.token}`)}
                data-active={containPaths([`${realm}${paths.token}`])}
              >
                <Elements.TextLink
                  data-active={containPaths([`${realm}${paths.token}`])}
                  weight='regular'
                  align='left'
                  size='md'
                >
                  {i18n.t('scenes.tokenctl.labels.digibeectl')}
                </Elements.TextLink>
              </Elements.Link>
            </Elements.ContentLink>
          </Role>
        </Elements.WrapperLink>
        <Role
          environmentCapable
          name={[
            'CONSUMER:READ',
            'GLOBAL:READ',
            'ACCOUNT:READ',
            'RELATION:READ',
            'REPLICA:READ',
            'APIKEY:READ'
          ]}
        >
          <div className='mb-4 mt-8 flex items-center gap-2'>
            {/* <Elements.IconTitle name='SlidersVSquare' /> */}
            <Settings className='icon-smallest' data-testid='icon' />
            <Elements.Title
              data-active={containPaths([
                `${realm}${paths.globals}`,
                `${realm}${paths.accounts}`,
                `${realm}${paths.api_keys}`,
                `${realm}${paths.relationship}`,
                `${realm}${paths.multi_instance}`,
                `${realm}${paths.capsule_headers}`,
                `${realm}${paths.data_streaming}`
              ])}
              align='left'
              size='lg'
              weight='medium'
            >
              {i18n.t('scenes.beta.labels.setting_plural')}
            </Elements.Title>
          </div>
        </Role>
        <Elements.WrapperLink>
          <Role name='GLOBAL:READ'>
            <Elements.ContentLink
              data-active={containPaths([
                `${realm}${paths.globals}`,
                ...(isBuildGlobalsEnabled
                  ? [`${realm}/operation/build/globals`]
                  : [])
              ])}
            >
              <Elements.Link
                to={
                  isBuildGlobalsEnabled
                    ? `/${realm}/operation/build/globals`
                    : `/${realm}${paths.globals}`
                }
                data-testid='settings-globals-link'
              >
                <Elements.TextLink
                  align='left'
                  weight='regular'
                  size='md'
                  data-active={containPaths([
                    `${realm}${paths.globals}`,
                    ...(isBuildGlobalsEnabled
                      ? [`${realm}/operation/build/globals`]
                      : [])
                  ])}
                >
                  {i18n.t('scenes.beta.labels.global_plural')}
                </Elements.TextLink>
              </Elements.Link>
            </Elements.ContentLink>
          </Role>
          <Role name='ACCOUNT:READ'>
            <Elements.ContentLink
              data-active={containPaths([
                `${realm}${paths.accounts}`,
                `${realm}${paths.oauth_providers}`,
                ...(isBuildAccountsEnabled
                  ? [`${realm}/operation/build/accounts`]
                  : [])
              ])}
            >
              <Elements.Link
                to={
                  isBuildAccountsEnabled
                    ? `/${realm}/operation/build/accounts`
                    : `/${realm}${paths.accounts}`
                }
              >
                <Elements.TextLink
                  data-active={containPaths([
                    `${realm}${paths.accounts}`,
                    `${realm}${paths.oauth_providers}`,
                    ...(isBuildAccountsEnabled
                      ? [`${realm}/operation/build/accounts`]
                      : [])
                  ])}
                  weight='regular'
                  align='left'
                  size='md'
                >
                  {i18n.t('label.account_plural')}
                </Elements.TextLink>
              </Elements.Link>
            </Elements.ContentLink>
          </Role>
          <Role name='APIKEY:READ' environmentCapable>
            <Elements.ContentLink
              data-active={containPaths([`${realm}${paths.api_keys}`])}
            >
              <Elements.Link flex to={`/${realm}${paths.api_keys}`}>
                <Elements.TextLink
                  data-active={containPaths([`${realm}${paths.api_keys}`])}
                  weight='regular'
                  align='left'
                  size='md'
                >
                  {i18n.t('action.consumers_api_keys')}
                </Elements.TextLink>
              </Elements.Link>
            </Elements.ContentLink>
          </Role>
          <Role name='RELATION:READ'>
            <Elements.ContentLink
              data-active={containPaths([`${realm}${paths.relationship}`])}
            >
              <Elements.Link to={`/${realm}${paths.relationship}`}>
                <Elements.TextLink
                  data-active={containPaths([`${realm}${paths.relationship}`])}
                  weight='regular'
                  align='left'
                  size='md'
                >
                  {i18n.t('scenes.beta.labels.relationship')}
                </Elements.TextLink>
              </Elements.Link>
            </Elements.ContentLink>
          </Role>
          <Role name='REPLICA:READ'>
            <Elements.ContentLink
              data-active={containPaths([`${realm}${paths.multi_instance}`])}
            >
              <Elements.Link to={`/${realm}${paths.multi_instance}`}>
                <Elements.TextLink
                  data-active={containPaths([
                    `${realm}${paths.multi_instance}`
                  ])}
                  weight='regular'
                  align='left'
                  size='md'
                >
                  {i18n.t('scenes.beta.labels.multi_instance')}
                </Elements.TextLink>
              </Elements.Link>
            </Elements.ContentLink>
          </Role>
          <Role name='CAPSULE:READ'>
            <Elements.ContentLink
              data-active={containPaths([`${realm}${paths.capsule_headers}`])}
            >
              <Elements.Link to={`/${realm}${paths.capsule_headers}`}>
                <Elements.TextLink
                  data-active={containPaths([
                    `${realm}${paths.capsule_headers}`
                  ])}
                  weight='regular'
                  align='left'
                  size='md'
                >
                  {i18n.t('noun.capsules_title')}
                </Elements.TextLink>
              </Elements.Link>
            </Elements.ContentLink>
          </Role>
          <Role name='CAPSULE:READ'>
            <Elements.ContentLink
              data-active={containPaths([`${realm}${paths.data_streaming}`])}
            >
              <Elements.Link to={`/${realm}${paths.data_streaming}`}>
                <Elements.TextLink
                  data-active={containPaths([
                    `${realm}${paths.data_streaming}`
                  ])}
                  weight='regular'
                  align='left'
                  size='md'
                >
                  {i18n.t('noun.data_streaming_title')}
                </Elements.TextLink>
              </Elements.Link>
            </Elements.ContentLink>
          </Role>
        </Elements.WrapperLink>

        <div className='mb-4 mt-8 flex items-center gap-2'>
          <ShieldEye className='icon-smallest' data-testid='icon' />
          <Elements.Title
            data-active={containPaths([`${realm}${paths.policies}`])}
            align='left'
            size='lg'
            weight='medium'
          >
            {i18n.t('label.governance')}
          </Elements.Title>
        </div>
        <Elements.WrapperLink>
          <Role name='POLICY:READ'>
            <Elements.ContentLink
              data-active={containPaths([`${realm}${paths.policies}`])}
              data-testid='governance-settings-policies-page-menu'
            >
              <Elements.Link
                data-testid='link'
                flex
                to={`/${realm}${paths.policies}/security`}
              >
                <Elements.TextLink
                  align='left'
                  weight='regular'
                  size='md'
                  data-active={containPaths([`${realm}${paths.policies}`])}
                  data-testid='text'
                >
                  {i18n.t('action.policies')}
                </Elements.TextLink>
              </Elements.Link>
            </Elements.ContentLink>
          </Role>
        </Elements.WrapperLink>

        {iff(license?.planName !== 'Production Subscription', () => (
          <>
            <div className='mb-4 mt-8 flex items-center gap-2'>
              <Coins className='icon-smallest' data-testid='icon' />
              <Elements.Title
                data-active={containPaths([`${realm}${paths.licensing}`])}
                align='left'
                size='lg'
                weight='medium'
              >
                {i18n.t('label.subscription')}
              </Elements.Title>
            </div>
            <Elements.WrapperLink>
              {iff(!showTogaiRedirectButton, () => (
                <Role name='LICENSE:READ'>
                  <Elements.ContentLink
                    data-active={containPaths([`${realm}${paths.licensing}`])}
                    data-testid='licensing-settings-licensing-page-menu'
                  >
                    <Elements.Link
                      data-testid='link'
                      flex
                      to={`/${realm}${paths.licensing}`}
                    >
                      <Elements.TextLink
                        align='left'
                        weight='regular'
                        size='md'
                        data-active={containPaths([
                          `${realm}${paths.licensing}`
                        ])}
                        data-testid='text'
                      >
                        {i18n.t('action.my_usage')}
                      </Elements.TextLink>
                    </Elements.Link>
                  </Elements.ContentLink>
                </Role>
              ))}
              {iff(
                license?.planName === 'Consumption Based Model' &&
                  !!togaiToken &&
                  showTogaiRedirectButton,
                () => (
                  <Role name='LICENSE:READ'>
                    <Elements.ContentLink
                      data-active={containPaths([`${realm}${paths.licensing}`])}
                      data-testid='licensing-settings-licensing-page-menu'
                    >
                      <Elements.Link
                        data-testid='link'
                        flex
                        to='#'
                        onClick={() =>
                          window
                            .open(
                              `${TOGAI_URL}/${TOGAI_ORGANIZATION_ID}?access-token=${encodeURIComponent(togaiToken)}`,
                              '_blank'
                            )
                            .focus()
                        }
                      >
                        <Elements.TextLink
                          align='left'
                          weight='regular'
                          externalLink
                          size='md'
                          data-active={containPaths([
                            `${realm}${paths.licensing}`
                          ])}
                          data-testid='text'
                        >
                          {i18n.t('action.my_usage')}
                        </Elements.TextLink>
                        <OpenNewWindow className='icon-smallest' />
                      </Elements.Link>
                    </Elements.ContentLink>
                  </Role>
                )
              )}
            </Elements.WrapperLink>
          </>
        ))}

        <div className='mb-4 mt-8 flex items-center gap-2'>
          <Bell className='icon-smallest' data-testid='icon' />
          <Elements.Title
            align='left'
            size='lg'
            weight='medium'
            data-active={containPaths([`${realm}${paths.alert}`])}
          >
            {i18n.t('label.notifications')}
          </Elements.Title>
        </div>
        <Elements.WrapperLink>
          <Role name='ALERT:READ'>
            <Elements.ContentLink
              data-active={containPaths([`${realm}${paths.alert}`])}
            >
              <Elements.Link
                flex
                to={`/${realm}${paths.monitor_alert}/${application.environments.allNames[0]}`}
                data-testid='alerts-settings-alerts-page-sidebar-menu'
              >
                <Elements.TextLink
                  align='left'
                  weight='regular'
                  size='md'
                  data-active={containPaths([
                    `${realm}${paths.alert}/${application.environments.allNames[0]}`
                  ])}
                >
                  {i18n.t('action.alerts')}
                </Elements.TextLink>
              </Elements.Link>
            </Elements.ContentLink>
          </Role>
        </Elements.WrapperLink>

        <Role name='USER:READ'>
          <div className='mb-4 mt-8 flex items-center gap-2'>
            <WindowLock className='icon-smallest' data-testid='icon' />
            <Elements.Title
              align='left'
              size='lg'
              weight='medium'
              data-active={containPaths([`${realm}${paths.credentials}`])}
            >
              {i18n.t('label.digibee_apis')}
            </Elements.Title>
          </div>
          <Elements.ContentLink
            data-active={containPaths([`${realm}${paths.credentials}`])}
          >
            <Elements.Link
              flex
              to={`/${realm}${paths.credentials}`}
              data-testid='monitor-api-management-settings-credentials-page-sidebar-menu'
            >
              <Elements.TextLink
                align='left'
                weight='regular'
                size='md'
                data-active={containPaths([`${realm}${paths.credentials}}`])}
              >
                {i18n.t('action.credentials')}
              </Elements.TextLink>
              <Elements.Badger>Beta</Elements.Badger>
            </Elements.Link>
          </Elements.ContentLink>
        </Role>
      </div>
      {isPowered && (
        <div className='flex justify-center pb-6 pt-4'>
          <img
            src={theme.images.gssGovernanceFooter}
            alt='GSS Governance Footer'
          />
        </div>
      )}
    </div>
  );
};

export default Sidebar;
